import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  AsyncRequestStatus,
  requestStatusFailure,
  requestStatusInProgress,
  requestStatusSuccess
} from '@portal/shared/store/request-status';

import { mapSortDescriptorToPayloadSorting } from '../individual-consumption-table.helpers';
import { ConsumptionAnalyticsFieldName } from '../individual-consumption-table.types';
import { IndividualConsumptionTableActions } from './individual-consumption-table.actions';
import {
  initialIndividualConsumptionTableState,
  IndividualConsumptionTableState
} from './individual-consumption-table.state';
import { Commodity } from '@portal/shared/types/commodity.types';

const individualConsumptionTableReducer = createReducer(
  initialIndividualConsumptionTableState,
  on(IndividualConsumptionTableActions.getGridData, (state): IndividualConsumptionTableState => ({
    ...state,
    tableDataRequest: requestStatusInProgress()
  })),
  on(
    IndividualConsumptionTableActions.getGridDataSuccess,
    (state, { data, total }): IndividualConsumptionTableState => ({
      ...state,
      tableData: { data, total },
      tableDataRequest: requestStatusSuccess()
    })
  ),
  on(IndividualConsumptionTableActions.getGridDataFailure, (state, { error }): IndividualConsumptionTableState => ({
    ...state,
    tableData: initialIndividualConsumptionTableState.tableData,
    tableDataRequest: requestStatusFailure(error)
  })),
  on(IndividualConsumptionTableActions.tableStateChange, (state, { tableState }): IndividualConsumptionTableState => {
    const { skip, sort, take } = tableState;
    const sortDescriptor = sort![0];

    return {
      ...state,
      tableState,
      ...(state.payload && {
        payload: {
          ...state.payload,
          skip,
          take,
          sort: mapSortDescriptorToPayloadSorting(sortDescriptor)
        }
      })
    };
  }),
  on(
    IndividualConsumptionTableActions.selectedMeasuringPeriodTabChange,
    (state, { tabTitle }): IndividualConsumptionTableState => {
      return {
        ...state,
        measuringPeriodTabs: state.measuringPeriodTabs.map(tab => ({ ...tab, isActive: tab.value === tabTitle })),
        ...(state.payload && {
          payload: {
            ...state.payload,
            filters: state.payload.filters?.map(filter => ({
              ...filter,
              filteredValues:
                filter.filterType === ConsumptionAnalyticsFieldName.MeasuringPeriod ? [tabTitle] : filter.filteredValues
            }))
          }
        })
      };
    }
  ),
  on(
    IndividualConsumptionTableActions.reportGroupIdChange,
    (state, { reportGroupId, commodity }): IndividualConsumptionTableState => {
      const { skip, sort, take } = state.tableState;
      const sortDescriptor = sort![0];

      return {
        ...state,
        payload: {
          skip,
          take,
          sort: mapSortDescriptorToPayloadSorting(sortDescriptor),
          filters: [
            {
              filterType: ConsumptionAnalyticsFieldName.MeasuringPeriod,
              filteredValues: [state.measuringPeriodTabs.find(tab => tab.isActive)?.value as string]
            },
            {
              filterType: ConsumptionAnalyticsFieldName.GroupId,
              filteredValues: [reportGroupId]
            },
            ...(commodity === Commodity.Electricity
              ? [
                  {
                    filterType: ConsumptionAnalyticsFieldName.FlowDirection,
                    filteredValues: [state.flowDirectionFilter.selected[0].value as string]
                  }
                ]
              : [])
          ]
        }
      };
    }
  ),
  on(
    IndividualConsumptionTableActions.flowDirectionFilterChange,
    (state, { flowDir }): IndividualConsumptionTableState => {
      return {
        ...state,
        flowDirectionFilter: {
          ...state.flowDirectionFilter,
          selected: [state.flowDirectionFilter.items.find(item => item.value === flowDir)!]
        },
        ...(state.payload && {
          payload: {
            ...state.payload,
            filters: state.payload.filters?.map(filter => ({
              ...filter,
              filteredValues:
                filter.filterType === ConsumptionAnalyticsFieldName.FlowDirection ? [flowDir] : filter.filteredValues
            }))
          }
        })
      };
    }
  )
);

export const individualConsumptionTableFeature = createFeature({
  name: 'individualConsumptionTable',
  reducer: individualConsumptionTableReducer,
  extraSelectors: ({ selectTableDataRequest }) => ({
    selectIsTableDataLoading: createSelector(
      selectTableDataRequest,
      request => request.status === AsyncRequestStatus.InProgress
    ),
    selectTableDataError: createSelector(selectTableDataRequest, request => request.error)
  })
});
